import { useState, useEffect, useCallback, useRef } from 'react'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
const API_URL = import.meta.env.VITE_API_URL || 'https://api.careersorter.co'

/**
 * Convert a base64 URL-safe string to a Uint8Array (for VAPID key).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

/**
 * Hook for managing Web Push notification subscriptions.
 *
 * States: 'unsupported' | 'loading' | 'default' | 'denied' | 'granted' | 'subscribed'
 */
export function usePushNotifications(authToken) {
  const [status, setStatus] = useState('loading')
  const subscriptionRef = useRef(null)

  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    VAPID_PUBLIC_KEY

  // Check current state on mount
  useEffect(() => {
    if (!isSupported) {
      setStatus('unsupported')
      return
    }

    async function checkStatus() {
      try {
        const permission = Notification.permission
        if (permission === 'denied') {
          setStatus('denied')
          return
        }

        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()

        if (subscription) {
          subscriptionRef.current = subscription
          // Verify with backend
          const res = await fetch(`${API_URL}/v1/push/verify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ endpoint: subscription.endpoint }),
          })
          const data = await res.json()
          setStatus(data.subscribed ? 'subscribed' : 'granted')
        } else {
          setStatus(permission === 'granted' ? 'granted' : 'default')
        }
      } catch {
        setStatus('default')
      }
    }

    if (authToken) checkStatus()
  }, [isSupported, authToken])

  const subscribe = useCallback(async () => {
    if (!isSupported || !authToken) return false

    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setStatus('denied')
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      })

      subscriptionRef.current = subscription
      const subJson = subscription.toJSON()

      const res = await fetch(`${API_URL}/v1/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
          user_agent: navigator.userAgent,
        }),
      })

      if (res.ok) {
        setStatus('subscribed')
        return true
      }
      return false
    } catch (err) {
      console.error('Push subscribe error:', err)
      return false
    }
  }, [isSupported, authToken])

  const unsubscribe = useCallback(async () => {
    if (!authToken) return false

    try {
      const subscription = subscriptionRef.current
      if (subscription) {
        await fetch(`${API_URL}/v1/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        })
        await subscription.unsubscribe()
        subscriptionRef.current = null
      }
      setStatus('default')
      return true
    } catch (err) {
      console.error('Push unsubscribe error:', err)
      return false
    }
  }, [authToken])

  return { status, subscribe, unsubscribe, isSupported }
}
